/**
 * Whether the reader has pointed at, touched or keyed into a chart frame.
 *
 * Recharts opens its tooltip on `mouseenter`, and the browser fires that event
 * when a chart mounts under a pointer that never moved: the click that opened
 * a page left the cursor where the new page draws its chart, and the tooltip
 * then sat over the bars as if pinned. A tooltip answers a reader's gesture, so
 * it shows only once the pointer moves, a finger or button presses, or the
 * chart takes focus (the accessibility layer reads the focused value out), and
 * closes again when a hovering pointer leaves.
 */
import React from 'react';

/** True outside a chart frame, so a chart drawn without one keeps its tooltip. */
export const ChartTooltipIntentContext = React.createContext(true);

export function useChartTooltipIntent(): boolean {
  return React.useContext(ChartTooltipIntentContext);
}

/** Tracks the reader's gesture on `frame`; false until the first real one. */
export function useChartTooltipIntentState(frameRef: React.RefObject<HTMLElement | null>): boolean {
  const [engaged, setEngaged] = React.useState(false);
  React.useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const engage = () => setEngaged(true);
    // A move with no displacement is the browser re-dispatching under a still
    // cursor, not the reader pointing.
    const onMove = (event: PointerEvent) => {
      if (event.movementX !== 0 || event.movementY !== 0) engage();
    };
    // A lifted finger also leaves the element, yet the tapped value should stay
    // readable, so only a pointer that hovers closes the tooltip by leaving.
    const disengage = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') setEngaged(false);
    };
    frame.addEventListener('pointermove', onMove);
    frame.addEventListener('pointerdown', engage);
    frame.addEventListener('keydown', engage);
    frame.addEventListener('focusin', engage);
    frame.addEventListener('pointerleave', disengage);
    return () => {
      frame.removeEventListener('pointermove', onMove);
      frame.removeEventListener('pointerdown', engage);
      frame.removeEventListener('keydown', engage);
      frame.removeEventListener('focusin', engage);
      frame.removeEventListener('pointerleave', disengage);
    };
  }, [frameRef]);
  return engaged;
}
